<section class="tt-detail-section mb-6" aria-labelledby="finance-compare-heading">
  <h2 id="finance-compare-heading" class="tt-detail-section__title mb-0">
    Srovnání prostavěnosti a plánovaných příjmů
  </h2>
  <p class="mt-1 mb-6 text-sm text-neutral-500 dark:text-neutral-400">Fyzický pokrok vs. Finanční krytí</p>

  <p *ngIf="progressError" class="text-xs text-amber-700 dark:text-amber-300 mb-4">{{ progressError }}</p>

  <div #chartRow class="grid grid-cols-1 gap-8 xl:grid-cols-3 xl:gap-0">
    <div class="min-w-0 xl:pr-6">
      <h3 class="finance-compare__kicker">Hlavní srovnávací ukazatel</h3>
      <div class="grid grid-cols-2 gap-3">
        <div *ngIf="model?.physical as physical" class="text-center min-w-0">
          <div class="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Prostavěnost</div>
          <div class="text-xs text-neutral-500 dark:text-neutral-400">(Fyzický pokrok)</div>
          <div class="relative mx-auto mt-2 h-36 w-36">
            <div #physicalHost class="absolute inset-0" aria-hidden="true"></div>
            <div class="pointer-events-none absolute inset-0 flex items-center justify-center">
              <span
                class="finance-compare__percent tabular-nums text-teal-700 dark:text-teal-300"
                [appCountUp]="physical.percentLabel"
              ></span>
            </div>
          </div>
          <p *ngIf="physical.caption" class="mt-2 text-xs text-neutral-600 dark:text-neutral-300">
            <span [appCountUp]="physical.caption"></span>
          </p>
        </div>

        <div *ngIf="model?.income as income" class="text-center min-w-0">
          <div class="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Plánovaný příjem</div>
          <div class="text-xs text-neutral-500 dark:text-neutral-400">(Finanční krytí)</div>
          <div class="relative mx-auto mt-2 h-36 w-36">
            <div #incomeHost class="absolute inset-0" aria-hidden="true"></div>
            <div class="pointer-events-none absolute inset-0 flex items-center justify-center">
              <span
                class="finance-compare__percent tabular-nums text-slate-900 dark:text-slate-100"
                [appCountUp]="income.percentLabel"
              ></span>
            </div>
          </div>
          <p *ngIf="income.caption" class="mt-2 text-xs text-neutral-600 dark:text-neutral-300">
            <span [appCountUp]="income.caption"></span>
          </p>
        </div>
      </div>

      <p
        *ngIf="model?.note as note"
        class="mt-4 rounded-xl px-3 py-3 text-center text-sm font-semibold"
        [ngClass]="
          note.tone === 'green'
            ? 'bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200'
            : 'bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200'
        "
        role="status"
      >
        <span [appCountUp]="note.text"></span>
      </p>
    </div>

    <div class="min-w-0 xl:border-l xl:px-6 finance-compare__rule">
      <h3 class="finance-compare__kicker">Porovnání podle kategorií</h3>
      <div *ngIf="model?.categories?.length" class="space-y-3">
        <div *ngFor="let cat of model?.categories ?? []; trackBy: trackCategory">
          <div class="mb-1 text-sm font-medium text-neutral-800 dark:text-neutral-100">{{ cat.label }}</div>
          <div *ngIf="cat.physicalLabel" class="mb-1 flex items-center gap-2">
            <div class="relative h-3.5 min-w-0 flex-1">
              <div
                class="finance-compare__bar absolute inset-y-0 left-0 rounded-full"
                [style.width.%]="barWidth(cat.physicalWidthPercent)"
                [style.background]="categoryColor(cat.id)"
                [style.transition]="barTransition"
              ></div>
            </div>
            <span
              class="w-40 shrink-0 text-right text-xs font-medium tabular-nums text-neutral-800 dark:text-neutral-100"
              [appCountUp]="cat.physicalLabel"
            ></span>
          </div>
          <div *ngIf="cat.incomeLabel" class="flex items-center gap-2">
            <div class="relative h-3.5 min-w-0 flex-1">
              <div
                class="finance-compare__bar finance-compare__income absolute inset-y-0 left-0 rounded-full"
                [style.width.%]="barWidth(cat.incomeWidthPercent)"
                [style.transition]="barTransition"
              ></div>
            </div>
            <span
              class="w-40 shrink-0 text-right text-xs font-medium tabular-nums text-neutral-800 dark:text-neutral-100"
              [appCountUp]="cat.incomeLabel"
            ></span>
          </div>
        </div>
      </div>
      <div *ngIf="showPhysicalLegend || showIncomeLegend" class="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-600 dark:text-neutral-300">
        <span *ngIf="showPhysicalLegend" class="inline-flex items-center gap-2">
          <span class="inline-block h-2.5 w-2.5 rounded-sm bg-blue-600" aria-hidden="true"></span>
          Prostavěnost (Fyzický stav)
        </span>
        <span *ngIf="showIncomeLegend" class="inline-flex items-center gap-2">
          <span class="finance-compare__income inline-block h-2.5 w-2.5 rounded-sm" aria-hidden="true"></span>
          Plánovaný příjem (Peníze)
        </span>
      </div>
    </div>

    <div class="min-w-0 xl:border-l xl:pl-6 finance-compare__rule">
      <h3 class="finance-compare__kicker">Souhrnné finanční ukazatele</h3>
      <div class="space-y-5">
        <div *ngFor="let line of model?.summary ?? []; trackBy: trackSummary">
          <div class="text-sm text-neutral-600 dark:text-neutral-400">{{ line.label }}</div>
          <div
            class="mt-0.5 text-3xl font-bold tabular-nums tracking-tight text-neutral-900 dark:text-neutral-50"
            [appCountUp]="line.valueLabel"
          ></div>
          <div
            *ngIf="line.detailLabel"
            class="text-sm font-medium text-neutral-500 dark:text-neutral-400"
            [appCountUp]="line.detailLabel"
          ></div>
        </div>
      </div>
    </div>
  </div>
</section>
